import { useMemo } from 'react';
import { POST_TYPE_AWARENESS_MULTIPLIER_MAP } from '../utils/awarenessMultipliers';

// --- Types ---
type RawPostInsight = {
    endDate?: string | Date;
    postType: string;
    likes?: number;
    comments?: number;
    shares?: number;
    clicks?: number;
    favorites?: number;
    closeups?: number;
  };

  type PostInsight = {
    endDate: Date;
    postType: string;
    likes?: number;
    comments?: number;
    shares?: number;
    clicks?: number;
    favorites?: number;
    closeups?: number;
  };

type HistoricalDataPoint = {
  dateTime: string;
  value: number;
};

const SOCIAL_INSTAGRAM_MULTIPLIERS = {
    likes: 0.15,
    comments: 1,
  };

  const SOCIAL_INSTAGRAM_STORY_MULTIPLIERS = {
    likes: 0.15,
    comments: 1,
    impressions: 0.05,
  };

  const SOCIAL_TIKTOK_MULTIPLIERS = {
    likes: 0.05,
    comments: 0.05,
    shares: 0.05,
  };

  const SOCIAL_YOUTUBE_MULTIPLIERS = {
    likes: 0.15,
    comments: 1,
  };

  const SOCIAL_YOUTUBE_SHORT_MULTIPLIERS = {
    likes: 0.05,
    comments: 0.05,
  };

  const SOCIAL_PINTEREST_MULTIPLIERS = {
    favorites: 3,
    clicks: 3,
    closeups: 0.05,
  };

  const SOCIAL_FACEBOOK_MULTIPLIERS = {
    likes: 0.15,
    comments: 1,
    shares: 1,
  };

  const SOCIAL_TWITTER_MULTIPLIERS = {
    likes: 0.15,
    comments: 1,
    shares: 1,
  };

  export const POST_TYPE_MULTIPLIER_MAP: Record<string, Record<string, number>> = {
    instagram_post: SOCIAL_INSTAGRAM_MULTIPLIERS,
    instagram_reel: SOCIAL_INSTAGRAM_MULTIPLIERS,
    instagram_story: SOCIAL_INSTAGRAM_STORY_MULTIPLIERS,
    instagram_video: SOCIAL_INSTAGRAM_MULTIPLIERS,
    tiktok_video: SOCIAL_TIKTOK_MULTIPLIERS,
    youtube_video: SOCIAL_YOUTUBE_MULTIPLIERS,
    youtube_short: SOCIAL_YOUTUBE_SHORT_MULTIPLIERS,
    pinterest: SOCIAL_PINTEREST_MULTIPLIERS,
    facebook: SOCIAL_FACEBOOK_MULTIPLIERS,
    twitter: SOCIAL_TWITTER_MULTIPLIERS,
  };

// --- Core calculation ---
function calcValue(data: PostInsight[]): number {
    return data.reduce((sum, item) => {
      const multipliers = POST_TYPE_MULTIPLIER_MAP[item.postType] ?? {};

      let itemValue = 0;
      // Go through each metric supported by the multiplier set
      for (const [metric, multiplier] of Object.entries(multipliers)) {
        const metricValue = (item as any)[metric] ?? 0;
        itemValue += metricValue * multiplier;
      }
      return sum + itemValue;
    }, 0);
  }
  // --- Awareness calculation ---
function calcAwarenessValue(data: PostInsight[]): number {
    return data.reduce((sum, item) => {
        const multipliers = POST_TYPE_AWARENESS_MULTIPLIER_MAP[item.postType] ?? {};

        let itemValue = 0;
        for (const [metric, multiplier] of Object.entries(multipliers)) {
        const metricValue = (item as any)[metric] ?? 0;
        itemValue += metricValue * multiplier;
        }

        return sum + itemValue;
    }, 0);
}

// --- Preprocessing to sanitize and convert data ---
function preprocessData(data?: RawPostInsight[] | null): PostInsight[] {
  if (!data) return [];
  return data
    .map((item) => {
      if (!item.endDate) return null;
      return {
        ...item,
        endDate: item.endDate instanceof Date ? item.endDate : new Date(item.endDate),
      };
    })
    .filter((item): item is PostInsight => !!item?.endDate && item.endDate instanceof Date);
}

// --- Hooks ---
export function useHistoricalData(
    rawData: RawPostInsight[] | null,
    intervalType: string,
  ): HistoricalDataPoint[] {
    return useMemo(() => {
      const insights = preprocessData(rawData);

      const getGroupKey = (date: Date) => {
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth();
        const day = date.getUTCDate();
        if (intervalType === 'MONTH') {
          const monthStr = (month + 1).toString().padStart(2, '0');
          return `${year}-${monthStr}-01`;
        }

        if (intervalType === 'WEEK') {
          const dayOfWeek = date.getUTCDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
          // Shift to Monday as first day of week (ISO standard)
          const diff = (dayOfWeek + 6) % 7;
          const startOfWeek = new Date(Date.UTC(year, month, day - diff));

          const weekYear = startOfWeek.getUTCFullYear();
          const weekMonth = (startOfWeek.getUTCMonth() + 1).toString().padStart(2, '0');
          const weekDay = startOfWeek.getUTCDate().toString().padStart(2, '0');
          return `${weekYear}-${weekMonth}-${weekDay}`;
        }

        // Default: group by exact UTC day
        const monthStr = (month + 1).toString().padStart(2, '0');
        const dayStr = day.toString().padStart(2, '0');
        return `${year}-${monthStr}-${dayStr}`;
      };

      const grouped = insights.reduce((acc, item) => {
        const dateStr = getGroupKey(item.endDate);
        if (!acc[dateStr]) acc[dateStr] = [];
        acc[dateStr].push(item);
        return acc;
      }, {} as Record<string, PostInsight[]>);

      return Object.entries(grouped)
        .map(([date, items]) => ({
          dateTime: `${date}T00:00:00.000Z`,
          value: calcValue(items),
        }))
        .sort((a, b) => a.dateTime.localeCompare(b.dateTime));
    }, [rawData, intervalType]);
  }
// --- Hooks ---
export function useHistoricalAwarenessData(
    rawData: RawPostInsight[] | null,
    intervalType: string,
  ): HistoricalDataPoint[] {
    return useMemo(() => {
      const insights = preprocessData(rawData);

      const getGroupKey = (date: Date) => {
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth();
        const day = date.getUTCDate();
        if (intervalType === 'MONTH') {
          const monthStr = (month + 1).toString().padStart(2, '0');
          return `${year}-${monthStr}-01`;
        }

        if (intervalType === 'WEEK') {
          const dayOfWeek = date.getUTCDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
          // Shift to Monday as first day of week (ISO standard)
          const diff = (dayOfWeek + 6) % 7;
          const startOfWeek = new Date(Date.UTC(year, month, day - diff));

          const weekYear = startOfWeek.getUTCFullYear();
          const weekMonth = (startOfWeek.getUTCMonth() + 1).toString().padStart(2, '0');
          const weekDay = startOfWeek.getUTCDate().toString().padStart(2, '0');
          return `${weekYear}-${weekMonth}-${weekDay}`;
        }

        // Default: group by exact UTC day
        const monthStr = (month + 1).toString().padStart(2, '0');
        const dayStr = day.toString().padStart(2, '0');
        return `${year}-${monthStr}-${dayStr}`;
      };

      const grouped = insights.reduce((acc, item) => {
        const dateStr = getGroupKey(item.endDate);
        if (!acc[dateStr]) acc[dateStr] = [];
        acc[dateStr].push(item);
        return acc;
      }, {} as Record<string, PostInsight[]>);

      return Object.entries(grouped)
        .map(([date, items]) => ({
          dateTime: `${date}T00:00:00.000Z`,
          value: calcAwarenessValue(items),
        }))
        .sort((a, b) => a.dateTime.localeCompare(b.dateTime));
    }, [rawData, intervalType]);
  }
